import React, { useMemo, useState } from 'react';
import { Chip, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { QuoteStatus, QuoteStatuses } from '../../types/quote-status.type';
import { QUOTE_STATUS_LABELS } from '../../constants/quote-status-labels.constant';

type ChipStyle = { backgroundColor: string; color: string };

const getChipStyle = (status: QuoteStatus): ChipStyle => {
  switch (status) {
    case QuoteStatuses.DRAFT:
      return { backgroundColor: '#f0f0f0', color: '#000000' };
    case QuoteStatuses.READY:
      return { backgroundColor: '#90caf9', color: '#000000' };
    case QuoteStatuses.SENT:
      return { backgroundColor: '#2196f3', color: '#ffffff' };
    case QuoteStatuses.ACCEPTED:
      return { backgroundColor: '#4caf50', color: '#ffffff' };
    case QuoteStatuses.REFUSED:
      return { backgroundColor: '#f44336', color: '#ffffff' };
    case QuoteStatuses.EXPIRED:
      return { backgroundColor: '#ff9800', color: '#ffffff' };
    case QuoteStatuses.CANCELLED:
      return { backgroundColor: '#9e9e9e', color: '#ffffff' };
    default:
      return { backgroundColor: '#f0f0f0', color: '#000000' };
  }
};

const ALLOWED_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  [QuoteStatuses.DRAFT]: [QuoteStatuses.READY, QuoteStatuses.CANCELLED],
  [QuoteStatuses.READY]: [QuoteStatuses.SENT, QuoteStatuses.CANCELLED],
  [QuoteStatuses.SENT]: [QuoteStatuses.ACCEPTED, QuoteStatuses.REFUSED, QuoteStatuses.CANCELLED],
  [QuoteStatuses.ACCEPTED]: [],
  [QuoteStatuses.REFUSED]: [],
  [QuoteStatuses.EXPIRED]: [],
  [QuoteStatuses.CANCELLED]: [],
};

const useStyles = makeStyles(() => ({
  root: (props: ChipStyle) => ({
    borderRadius: 4,
    textTransform: 'capitalize',
    backgroundColor: props.backgroundColor,
    color: props.color,
    '&:hover, &:focus': {
      backgroundColor: props.backgroundColor,
    },
  }),
}));

type Props = {
  status: QuoteStatus;
  size?: 'small' | 'medium';
  onChange?: (nextStatus: QuoteStatus) => void | Promise<void>;
};

export function QuoteStatusChip({ status, size = 'small', onChange }: Props) {
  const { label, backgroundColor, color } = useMemo(
    () => ({ label: QUOTE_STATUS_LABELS[status], ...getChipStyle(status) }),
    [status],
  );
  const classes = useStyles({ backgroundColor, color });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const allowedNext = ALLOWED_TRANSITIONS[status];
  const isInteractive = Boolean(onChange) && allowedNext.length > 0;

  if (!isInteractive) {
    return <Chip className={classes.root} label={label} size={size} />;
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = async (nextStatus: QuoteStatus) => {
    setAnchorEl(null);
    await onChange?.(nextStatus);
  };

  return (
    <>
      <Chip className={classes.root} label={label} size={size} onClick={handleOpen} />
      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {allowedNext.map((nextStatus) => (
          <MenuItem key={nextStatus} onClick={() => handleSelect(nextStatus)}>
            {QUOTE_STATUS_LABELS[nextStatus]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
